import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { placeNames } from '@/utils/places';
import { zodResolver } from '@/utils/zodResolver';
import { Field } from '@/components/ui/Field';
import {
  MAX_PASSENGERS,
  rideSearchSchema,
  todayIsoDate,
  type RideSearchInput,
  type RideSearchValues,
} from '../schemas/rideSearch';

export interface RideSearchFormProps {
  onSearch: (values: RideSearchValues) => void;
  defaultValues?: Partial<RideSearchInput>;
  submitting?: boolean;
}

/**
 * The product's front door: From, To, Date, Passengers.
 *
 * <p>Wired to Zod through the shared `zodResolver` (no `@hookform/resolvers` dependency). One
 * schema, one call site -- a dependency to save eight lines is not worth the supply-chain
 * surface or the version coupling. If a second form needs it, the resolver moves to a
 * shared module; if a fifth does, the package earns its place.
 *
 * <p>Autocomplete against a maps provider replaces the plain text inputs in Phase 4;
 * `LocationService` already exists on the backend for it. The field contract does not
 * change when that lands, so this form does not need rewriting.
 */
export function RideSearchForm({ onSearch, defaultValues, submitting }: RideSearchFormProps) {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<RideSearchInput>({
    defaultValues: {
      from: '',
      to: '',
      date: todayIsoDate(),
      passengers: 1,
      ...defaultValues,
    },
    resolver: zodResolver<RideSearchInput>(rideSearchSchema),
  });

  const swapPlaces = () => {
    const { from, to } = getValues();
    setValue('from', to, { shouldValidate: true, shouldDirty: true });
    setValue('to', from, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <form
      // noValidate: the browser's own bubbles would pre-empt our accessible, styled
      // messages and cannot be made consistent across browsers.
      noValidate
      onSubmit={handleSubmit((values) => onSearch(values as unknown as RideSearchValues))}
      aria-label="Search for a ride"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_auto_auto] lg:items-start lg:gap-3"
    >
      <div className="relative grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2 lg:col-span-2 lg:gap-3">
        <Field
          label="From"
          placeholder="Delhi"
          list="place-suggestions"
          autoComplete="off"
          error={errors.from?.message}
          {...register('from')}
        />
        <Field
          label="To"
          placeholder="Chandigarh"
          list="place-suggestions"
          autoComplete="off"
          error={errors.to?.message}
          {...register('to')}
        />
        <button
          type="button"
          onClick={swapPlaces}
          aria-label="Swap origin and destination"
          className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4 rotate-90 transition-transform sm:rotate-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 7h11l-3-3M17 17H6l3 3" />
          </svg>
        </button>
      </div>
      <Field
        label="Date"
        type="date"
        min={todayIsoDate()}
        className="lg:w-44"
        error={errors.date?.message}
        {...register('date')}
      />
      <Field
        label="Seats"
        type="number"
        inputMode="numeric"
        min={1}
        max={MAX_PASSENGERS}
        className="lg:w-24"
        error={errors.passengers?.message}
        {...register('passengers')}
      />

      <datalist id="place-suggestions">
        {placeNames().map((n) => <option key={n} value={n} />)}
      </datalist>

      <div className="sm:col-span-2 lg:col-span-1 lg:self-start lg:pt-7">
        <Button type="submit" size="lg" loading={submitting} className="w-full lg:w-auto">
          Search
        </Button>
      </div>
    </form>
  );
}
